import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown,
  Camera, Video
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';

const Camaras = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'camaras');
  const [camarasList, setCamarasList] = useState([]);
  const [stats, setStats] = useState({ total: 0, dahua: 0, hikvision: 0, axis: 0, otros: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', site: '', brand: '', dvr: '', status_id: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para debounce
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  const [pageSize, setPageSize] = useState(25);

  // Form options
  const [options, setOptions] = useState({ 
    marcas: [], sites: [], ubicaciones: [], tipos_conexion: [], 
    estados: [], proveedores: [], dvrs: [] 
  });

  // Filter options (only values that exist in camaras table)
  const [filterOptions, setFilterOptions] = useState({ 
    marcas: [], sites: [], dvrs: [], estados: [] 
  });

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, camara: null });
  const [editModal, setEditModal] = useState({ open: false, camara: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, camara: null });
  const [formData, setFormData] = useState({
    name: '', model: '', ip_address: '', mac_address: '', zone: '',
    installation_date: '', notes: '', site_id: '', location_id: '',
    connection_type_id: '', status_id: '', provider_id: '', brand_id: '',
    rtsp_port: '554', http_port: '80', dvr_id: ''
  });
  const [saving, setSaving] = useState(false);

  // Bulk selection
  const {
    selectedIds,
    isAllSelected,
    isPartialSelected,
    toggleItem,
    toggleAll,
    clearSelection
  } = useBulkSelection(camarasList);

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/camaras/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/assets/camaras/form/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/camaras/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchCamarasList = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.site) params.append('site', filters.site);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.dvr) params.append('dvr', filters.dvr);
      if (filters.status_id) params.append('status_id', filters.status_id);

      const response = await api.get(`/assets/camaras?${params}`);
      setCamarasList(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar cámaras');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchCamarasList();
  }, [page, pageSize, filters.search, filters.site, filters.brand, filters.dvr, filters.status_id, sortConfig]);

  // Debounce para filtro de búsqueda (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Marca badge
  const getMarcaBadge = (marca) => {
    if (!marca) return <span className="badge badge-outline">Sin marca</span>;
    const m = marca.toLowerCase();
    if (m.includes('dahua')) return <span className="badge badge-success">Dahua</span>;
    if (m.includes('hikvision')) return <span className="badge badge-info">Hikvision</span>;
    if (m.includes('axis')) return <span className="badge badge-warning">Axis</span>;
    if (m.includes('hanwha')) return <span className="badge badge-primary">Hanwha</span>;
    return <span className="badge badge-outline">{marca}</span>;
  };

  // View modal
  const handleView = (camara) => {
    setViewModal({ open: true, camara });
  };

  // Edit modal
  const handleEdit = (camara) => {
    setFormData({
      name: camara.name || '',
      model: camara.model || '',
      ip_address: camara.ip_address || '',
      mac_address: camara.mac_address || '',
      zone: camara.zone || '',
      installation_date: camara.installation_date || '',
      notes: camara.notes || '',
      site_id: camara.site_id?.toString() || '',
      location_id: camara.location_id?.toString() || '',
      connection_type_id: camara.connection_type_id?.toString() || '',
      status_id: camara.status_id?.toString() || '',
      provider_id: camara.provider_id?.toString() || '',
      brand_id: camara.brand_id?.toString() || '',
      rtsp_port: camara.rtsp_port?.toString() || '554',
      http_port: camara.http_port?.toString() || '80',
      dvr_id: camara.dvr_id?.toString() || ''
    });
    setEditModal({ open: true, camara, isCopy: false });
  };

  // Copy modal
  const handleCopy = (camara) => {
    setFormData({
      name: '', // Unique - clear
      model: camara.model || '', // Copy
      ip_address: '', // Unique - clear
      mac_address: '', // Unique - clear
      zone: camara.zone || '', // Copy
      installation_date: '', // Clear
      notes: camara.notes || '', // Copy
      site_id: camara.site_id?.toString() || '', // Copy
      location_id: camara.location_id?.toString() || '', // Copy
      connection_type_id: camara.connection_type_id?.toString() || '', // Copy
      status_id: camara.status_id?.toString() || '', // Copy
      provider_id: camara.provider_id?.toString() || '', // Copy
      brand_id: camara.brand_id?.toString() || '', // Copy
      rtsp_port: camara.rtsp_port?.toString() || '554', // Copy
      http_port: camara.http_port?.toString() || '80', // Copy
      dvr_id: camara.dvr_id?.toString() || '' // Copy
    });
    setEditModal({ open: true, camara: null, isCopy: true });
  };

  // Create new
  const handleCreate = () => {
    setFormData({
      name: '', model: '', ip_address: '', mac_address: '', zone: '',
      installation_date: '', notes: '', site_id: '', location_id: '',
      connection_type_id: '', status_id: '', provider_id: '', brand_id: '',
      rtsp_port: '554', http_port: '80', dvr_id: ''
    });
    setEditModal({ open: true, camara: null, isCopy: false });
  };

  // Save
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        connection_type_id: formData.connection_type_id ? parseInt(formData.connection_type_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        rtsp_port: formData.rtsp_port ? parseInt(formData.rtsp_port) : 554,
        http_port: formData.http_port ? parseInt(formData.http_port) : 80,
        dvr_id: formData.dvr_id ? parseInt(formData.dvr_id) : null,
        installation_date: formData.installation_date || null
      };

      if (editModal.camara && !editModal.isCopy) {
        await api.put(`/assets/camaras/${editModal.camara.id}`, payload);
        toast.success('Cámara actualizada exitosamente');
      } else {
        await api.post('/assets/camaras', payload);
        toast.success('Cámara creada exitosamente');
      }
      setEditModal({ open: false, camara: null, isCopy: false });
      fetchCamarasList();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/camaras/${deleteModal.camara.id}`);
      toast.success('Cámara eliminada exitosamente');
      setDeleteModal({ open: false, camara: null });
      fetchCamarasList();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Get unique marcas for filter
  const uniqueMarcas = [...new Set(options.marcas.map(m => m.nombre).filter(Boolean))];

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Cámaras</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Camaras</h1>
            <p className="page-subtitle">Gestion de camaras de seguridad y videovigilancia</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/camaras"
              filters={{ search: filters.search }}
              filename="camaras"
            />
            {canCreate && (
              <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-camara-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Camara
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="camaras-stats">
          <div className="stats-card flex items-center gap-3" data-testid="stats-total">
            <Camera className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-dahua">
            <Video className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.dahua}</div>
              <div className="stats-card-label">Dahua</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-hikvision">
            <Video className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.hikvision}</div>
              <div className="stats-card-label">Hikvision</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-axis">
            <Video className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.axis}</div>
              <div className="stats-card-label">Axis</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-otros">
            <Video className="h-6 w-6 text-gray-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-gray-500">{stats.otros}</div>
              <div className="stats-card-label">Otros</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isPartialSelected}
                            onCheckedChange={toggleAll}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            NOMBRE
                            <SortIcon field="name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip_address')}
                        >
                          <div className="flex items-center gap-1">
                            IP
                            <SortIcon field="ip_address" />
                          </div>
                        </th>
                        <th className="text-center">MARCA</th>
                        <th>MODELO</th>
                        <th>ZONA</th>
                        <th>DVR/NVR</th>
                        <th>SITE</th>
                        <th>ESTADO</th>
                        <th className="w-28 text-center">ACCIONES</th>
                      </tr>
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar en todos los campos..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.brand || undefined}
                            onValueChange={(v) => handleFilterChange('brand', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {uniqueMarcas.map(marca => (
                                <SelectItem key={marca} value={marca}>{marca}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.dvr || undefined}
                            onValueChange={(v) => handleFilterChange('dvr', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              <SelectItem value="sin_dvr">Sin DVR</SelectItem>
                              {filterOptions.dvrs?.map(dvr => (
                                <SelectItem key={dvr.id} value={dvr.id.toString()}>{dvr.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.site || undefined}
                            onValueChange={(v) => handleFilterChange('site', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {filterOptions.sites?.map(site => (
                                <SelectItem key={site.id} value={site.id.toString()}>{site.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.status_id || undefined}
                            onValueChange={(v) => handleFilterChange('status_id', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {filterOptions.estados?.map(e => (
                                <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {camarasList.length === 0 ? (
                        <tr>
                          <td colSpan={10}>
                            <div className="empty-state">
                              <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron camaras</p>
                            </div>
                          </td>
                        </tr>
                      ) : camarasList.map((camara) => (
                        <tr key={camara.id} data-testid={`camara-row-${camara.id}`}>
                          <td>
                            <Checkbox
                              checked={selectedIds.includes(camara.id)}
                              onCheckedChange={() => toggleItem(camara.id)}
                              data-testid={`select-camara-${camara.id}`}
                            />
                          </td>
                          <td className="font-medium text-primary">{camara.name}</td>
                          <td className="font-mono text-sm text-muted-foreground">{camara.ip_address || '-'}</td>
                          <td className="text-center">{getMarcaBadge(camara.marca_nombre)}</td>
                          <td className="text-muted-foreground">{camara.model || '-'}</td>
                          <td className="text-muted-foreground">{camara.zone || '-'}</td>
                          <td>
                            {camara.dvr_nombre ? (
                              <span className="badge badge-info">{camara.dvr_nombre}</span>
                            ) : (
                              <span className="text-muted-foreground text-xs">Sin DVR</span>
                            )}
                          </td>
                          <td className="text-muted-foreground">{camara.site_nombre || '-'}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              camara.status_nombre?.toLowerCase().includes('activo') 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : camara.status_nombre?.toLowerCase().includes('baja') || camara.status_nombre?.toLowerCase().includes('inactivo')
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {camara.status_nombre || 'Sin estado'}
                            </span>
                          </td>
                          <td className="w-28">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => handleView(camara)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-camara-${camara.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(camara)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-camara-${camara.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(camara)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title="Copiar"
                                data-testid={`copy-camara-${camara.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, camara })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-camara-${camara.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages || totalPages === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* View Modal */}
        <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, camara: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-primary" strokeWidth={1.5} />
                {viewModal.camara?.name}
              </DialogTitle>
            </DialogHeader>
            {viewModal.camara && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Nombre</Label>
                    <p className="font-medium">{viewModal.camara.name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">IP</Label>
                    <p className="font-mono">{viewModal.camara.ip_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">MAC</Label>
                    <p className="font-mono text-sm">{viewModal.camara.mac_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Marca</Label>
                    <div className="mt-1">{getMarcaBadge(viewModal.camara.marca_nombre)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Modelo</Label>
                    <p className="text-sm">{viewModal.camara.model || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Zona</Label>
                    <p className="text-sm">{viewModal.camara.zone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Puertos</Label>
                    <p className="text-sm font-mono">RTSP: {viewModal.camara.rtsp_port} | HTTP: {viewModal.camara.http_port}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Site</Label>
                    <p>{viewModal.camara.site_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ubicacion</Label>
                    <p>{viewModal.camara.ubicacion_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Tipo Conexion</Label>
                    <p>{viewModal.camara.conexion_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estado</Label>
                    <p>{viewModal.camara.status_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Proveedor</Label>
                    <p>{viewModal.camara.proveedor_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">DVR/NVR</Label>
                    <p>{viewModal.camara.dvr_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Fecha Instalacion</Label>
                    <p>{viewModal.camara.installation_date || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Notas</Label>
                    <p className="text-sm text-muted-foreground">{viewModal.camara.notes || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit/Create/Copy Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, camara: null, isCopy: false })}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-primary" strokeWidth={1.5} />
                {editModal.isCopy ? 'Copiar Camara' : editModal.camara ? 'Editar Camara' : 'Nueva Camara'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-3 gap-4">
              {/* Unique fields */}
              <div className="space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre de la cámara"
                  data-testid="input-name"
                  data-validation={editModal.isCopy ? (!formData.name ? 'required-empty' : '') : (!formData.name ? 'required-empty' : 'valid')}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">IP</Label>
                <Input
                  value={formData.ip_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                  placeholder="192.168.1.100"
                  data-testid="input-ip"
                  data-validation={editModal.isCopy ? (!formData.ip_address ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">MAC</Label>
                <Input
                  value={formData.mac_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, mac_address: e.target.value }))}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  data-testid="input-mac"
                  data-validation={editModal.isCopy ? (!formData.mac_address ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              {/* Copy fields */}
              <div className="space-y-1">
                <Label className="text-xs">Marca</Label>
                <Select
                  value={formData.brand_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-marca" data-validation={editModal.isCopy && formData.brand_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar marca" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin marca</SelectItem>
                    {options.marcas.map(marca => (
                      <SelectItem key={marca.id} value={marca.id.toString()}>{marca.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Modelo</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Modelo de la cámara"
                  data-testid="input-model"
                  data-validation={editModal.isCopy && formData.model ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Zona</Label>
                <Input
                  value={formData.zone}
                  onChange={(e) => setFormData(prev => ({ ...prev, zone: e.target.value }))}
                  placeholder="Zona o sector"
                  data-testid="input-zone"
                  data-validation={editModal.isCopy && formData.zone ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Site</Label>
                <Select
                  value={formData.site_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-site" data-validation={editModal.isCopy && formData.site_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin site</SelectItem>
                    {options.sites.map(site => (
                      <SelectItem key={site.id} value={site.id.toString()}>{site.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Ubicacion</Label>
                <Select
                  value={formData.location_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-ubicacion" data-validation={editModal.isCopy && formData.location_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar ubicacion" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin ubicacion</SelectItem>
                    {options.ubicaciones.map(ub => (
                      <SelectItem key={ub.id} value={ub.id.toString()}>{ub.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Tipo Conexion</Label>
                <Select
                  value={formData.connection_type_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, connection_type_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-conexion" data-validation={editModal.isCopy && formData.connection_type_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin tipo</SelectItem>
                    {options.tipos_conexion.map(tc => (
                      <SelectItem key={tc.id} value={tc.id.toString()}>{tc.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <Select
                  value={formData.status_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-estado" data-validation={editModal.isCopy && formData.status_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin estado</SelectItem>
                    {options.estados.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Proveedor</Label>
                <Select
                  value={formData.provider_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, provider_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-proveedor" data-validation={editModal.isCopy && formData.provider_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin proveedor</SelectItem>
                    {options.proveedores.map(p => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">DVR/NVR</Label>
                <Select
                  value={formData.dvr_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, dvr_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-dvr" data-validation={editModal.isCopy && formData.dvr_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar DVR" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin DVR</SelectItem>
                    {options.dvrs.map(d => (
                      <SelectItem key={d.id} value={d.id.toString()}>{d.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Puerto RTSP</Label>
                <Input
                  type="number"
                  value={formData.rtsp_port}
                  onChange={(e) => setFormData(prev => ({ ...prev, rtsp_port: e.target.value }))}
                  placeholder="554"
                  data-testid="input-rtsp"
                  data-validation={editModal.isCopy && formData.rtsp_port ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Puerto HTTP</Label>
                <Input
                  type="number"
                  value={formData.http_port}
                  onChange={(e) => setFormData(prev => ({ ...prev, http_port: e.target.value }))}
                  placeholder="80"
                  data-testid="input-http"
                  data-validation={editModal.isCopy && formData.http_port ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Fecha Instalacion</Label>
                <Input
                  type="date"
                  value={formData.installation_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, installation_date: e.target.value }))}
                  data-testid="input-fecha"
                />
              </div>
              
              <div className="col-span-3 space-y-1">
                <Label className="text-xs">Notas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={2}
                  data-testid="input-notes"
                  data-validation={editModal.isCopy && formData.notes ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, camara: null, isCopy: false })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, camara: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Camara
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar la camara <strong>{deleteModal.camara?.name}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, camara: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchCamarasList(); fetchStats(); }}
          endpoint="/assets/camaras"
          options={{
            sites: options.sites,
            dvrs: options.dvrs,
            statuses: options.estados,
            brands: options.marcas,
            ubicaciones: options.ubicaciones
          }}
          fields={['site_id', 'dvr_id', 'status_id', 'brand_id', 'location_id']}
        />
      </div>
    </Layout>
  );
};

export default Camaras;
